import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import sha1 from 'crypto-js/sha1';
import LogRocket from 'logrocket';
import axios from '@api';
import { loadIntercom } from './utilities/helpers';

const Intercom = () => {
  const { data, isDataLoaded } = useSelector((state) => state.user);
  const { clientInfo, email, firstName, id, lastName } = data;
  const [userHash, setUserHash] = useState(null);
  const [backendUserId, setBackendUserId] = useState(null);
  const [isLoadingHash, setIsLoadingHash] = useState(false);
  const [hasAttempted, setHasAttempted] = useState(false);

  const MAX_RETRIES = 3;
  const RETRY_DELAY = 1000; // 1 second

  // Fetch secure user hash from backend with retry logic
  const fetchUserHash = async (attempt = 1) => {
    try {
      setIsLoadingHash(true);

      const response = await axios({
        method: 'get',
        url: '/api/intercom/user-hash',
        withCredentials: true,
      });

      const data = response.data;

      // Handle both possible response formats
      const userHashValue = data.user_hash || data.userHash;
      const userIdValue = data.user_id || data.userId;

      if (!userHashValue) {
        throw new Error('Invalid API response format');
      }

      if (!userIdValue) {
        throw new Error('Invalid API response format');
      }

      setUserHash(userHashValue);
      setBackendUserId(userIdValue);
      setIsLoadingHash(false);
    } catch (error) {
      if (attempt < MAX_RETRIES) {
        setTimeout(() => {
          fetchUserHash(attempt + 1);
        }, RETRY_DELAY * attempt); // Exponential backoff
      } else {
        console.error(
          'Intercom widget failed to initialize due to authentication issues. Please contact support if this persists.'
        );
        setIsLoadingHash(false);
        setHasAttempted(true);
        // No fallback - Intercom will not load without proper hash
      }
    }
  };

  useEffect(() => {
    if (isDataLoaded && !userHash && !isLoadingHash && !hasAttempted) {
      setHasAttempted(true);
      fetchUserHash();
    }
  }, [isDataLoaded, userHash, isLoadingHash, hasAttempted]);

  useEffect(() => {
    if (!window.Intercom) {
      loadIntercom();
    }

    // Only initialize Intercom when we have user data AND successfully fetched the user hash
    // NO FALLBACK - Intercom will not load without proper identity verification
    if (isDataLoaded && userHash && backendUserId) {
      const intercomConfig = {
        app_id: process.env.REACT_APP_INTERCOM,
        company: {
          id: sha1(clientInfo?.name).toString(),
          name: clientInfo?.name,
        },
        email,
        name: `${firstName} ${lastName}`,
        user_hash: userHash, // REQUIRED: Identity verification hash
        user_id: backendUserId.toString(), // CRITICAL: Use the SAME user_id that was used to generate the hash
      };

      window.Intercom('boot', intercomConfig);

      LogRocket.getSessionURL((sessionURL) => {
        window.Intercom('trackEvent', 'LogRocket', { sessionURL });
      });

      return () => window.Intercom('shutdown');
    }
  }, [isDataLoaded, userHash, backendUserId, clientInfo, email, firstName, id, lastName]);

  return null;
};

export default Intercom;
